<template>
    <div>
        <title-card :title="data.name" help="sub-panel-box">

            <template slot="buttons">

                <v-tooltip top color="primary" v-if="hasPermission('update sub panels')">
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on"
                               icon
                               @click="editTranslationsClicked">
                            <v-icon>mdi-earth</v-icon>
                        </v-btn>
                    </template>
                    <span>Edit generic translations</span>
                </v-tooltip>

                <v-tooltip top color="primary" v-if="hasPermission('update sub panels')">
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on"
                               icon
                               @click="editEmailsClicked">
                            <v-icon>mdi-email-outline</v-icon>
                        </v-btn>
                    </template>
                    <span>Configure email templates</span>
                </v-tooltip>

                <v-tooltip top color="primary" v-if="hasPermission('update sub panels')">
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on"
                               icon
                               @click="editLandingPagesClicked">
                            <v-icon>mdi-card-text-outline</v-icon>
                        </v-btn>
                    </template>
                    <span>Configure landing pages</span>
                </v-tooltip>

                <v-tooltip top color="primary" v-if="hasPermission('update sub panels')">
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on"
                               icon
                               @click="openSettings">
                            <v-icon>mdi-cog</v-icon>
                        </v-btn>
                    </template>
                    <span>Edit settings</span>
                </v-tooltip>

            </template>

            <v-container fluid class="pa-0">
                <v-row dense>

                    <v-col cols="12">

                        <v-alert class="body-2 mb-1" type="info"
                                 v-if="!imageData && !data.logo_url && hasPermission('update sub panels')">
                            Upload logo! We'll use it to automatically theme emails and landing pages!
                        </v-alert>

                        <v-card color="white"
                                @mouseover="showEditLogoButton(true)"
                                @mouseout="showEditLogoButton(false)">

                            <v-card v-if="!imageData && !data.logo_url"
                                    light color="white"
                                    max-width="900"
                                    max-height="300"
                                    class="text-center">
                                <div style="display: inline-block;">
                                    <v-icon size="100">mdi-image-area</v-icon>
                                </div>
                            </v-card>

                            <v-img v-if="!imageData && data.logo_url"
                                   contain
                                   :aspect-ratio="3"
                                   :max-width="900"
                                   :src="data.logo_url"/>

                            <v-img v-if="imageData"
                                   contain
                                   :aspect-ratio="3"
                                   :max-width="900"
                                   :src="imageData"/>

                            <v-fab-transition v-if="hasPermission('update sub panels')">
                                <v-btn v-show="editButtonVisible"
                                       @mouseover="showEditLogoButton(true)"
                                       color="primary"
                                       dark
                                       small
                                       absolute
                                       bottom
                                       right
                                       fab
                                       style="margin-bottom: 35px"
                                       @click="editLogoClicked()"
                                >
                                    <v-tooltip top color="primary">
                                        <template v-slot:activator="{ on }">
                                            <v-icon v-on="on">mdi-upload</v-icon>
                                        </template>
                                        <span>Upload logo</span>
                                    </v-tooltip>
                                </v-btn>
                            </v-fab-transition>
                        </v-card>
                    </v-col>

                    <!-- SUBSCRIBED MEMBERS -->
                    <v-col cols="12" md="6" lg="6">
                        <stat-box-numerical hint="Subscribed members"
                                            :value="data.member_count"
                                            color="blue-grey lighten-1">
                        </stat-box-numerical>
                    </v-col>

                    <v-col cols="12" md="6" lg="6">

                        <!-- REGISTRATION SURVEY APP -->
                        <v-card
                            :color="data.registration_survey && data.registration_survey.enabled ? 'green darken-2' : 'grey darken-3'"
                            height="46"
                            @click="registrationLinksClicked"
                            class="cursor-pointer">
                            <div class="d-flex pa-2">
                                <div class="flex-shrink-1 align-self-center">
                                    <v-icon size="30"
                                            :color="data.registration_survey && data.registration_survey.enabled ? 'white' : 'grey'">
                                        mdi-account-question
                                    </v-icon>
                                </div>
                                <div class="flex-grow-1 align-self-center ml-3">
                                    <span v-if="data.registration_survey && data.registration_survey.enabled">
                                        Registration Survey
                                    </span>
                                    <span v-else class="grey--text">
                                        Registration Survey
                                    </span>
                                </div>
                            </div>
                        </v-card>

                        <!-- MEMBERS APP ENABLED -->
                        <v-card
                            :color="data.app_enabled ? 'green darken-2' : 'grey darken-3'"
                            height="46"
                            @click="membersAppClicked"
                            class="mt-2 cursor-pointer">
                            <div class="d-flex pa-2">
                                <div class="flex-shrink-1 align-self-center">
                                    <v-icon size="30" :color="data.app_enabled ? 'white' : 'grey'">
                                        mdi-monitor-cellphone-star
                                    </v-icon>
                                </div>
                                <div class="flex-grow-1 align-self-center ml-3">
                                    <span v-if="data.app_enabled">
                                        Members App
                                    </span>
                                    <span v-else class="grey--text">
                                        Members App
                                    </span>
                                </div>
                            </div>
                        </v-card>
                    </v-col>

                    <!-- COMMUNITY ADDRESS -->
                    <v-col cols="12" md="12" lg="12" v-if="data.app_url === false">

                        <v-card flat color="grey darken-3" class="pa-3 pointer"
                                @click="communityUrlClicked">
                            <v-tooltip top color="primary">
                                <template v-slot:activator="{ on }">
                                    <v-icon class="mr-2" color="grey">mdi-link</v-icon>
                                    <span v-on="on">{{ computedCommunityUrl }}</span>
                                </template>

                                <span>Click to open up community site</span>

                            </v-tooltip>
                        </v-card>

                    </v-col>

                    <!-- LOCALES -->
                    <v-col cols="12">

                        <v-container fluid class="pa-0">
                            <v-row dense>

                                <v-col cols="12" class="pt-0">

                                    <v-card class="d-flex flex-wrap align-content-start pa-2">
                                        <div v-for="locale in data.locales"
                                             :key="locale.id"
                                             class="mr-1"
                                        >
                                            <locale-flag
                                                :locale="locale"
                                            ></locale-flag>
                                            <v-card width="70" flat style="margin: 0 auto;"
                                                color="grey darken-2"
                                                    class="mt-1"
                                                    small
                                                    label>
                                                <div class="text-center grey--text text--lighten-1">
                                                {{ locale.code }}
                                                </div>
                                            </v-card>
                                        </div>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-col>

                </v-row>
            </v-container>

            <json v-if="debug" :data="data"></json>

        </title-card>


        <v-dialog fullscreen
                  persistent
                  no-click-animation
                  v-model="configureLandingModel">

            <v-card>
                <landing-page-configurator
                    :key="'landing-editor-' + landingEditorKey"
                    :sub-panel="data"
                    @close="configureLandingModel=false"
                ></landing-page-configurator>
            </v-card>
        </v-dialog>

        <v-dialog
            fullscreen
            persistent
            no-click-animation
            v-model="configureEmailModel">
            <v-card>
                <email-theme-configurator
                    :key="'email-editor-' + emailEditorKey"
                    :sub-panel="data"
                    @close="configureEmailModel=false"
                ></email-theme-configurator>
            </v-card>
        </v-dialog>

        <upload-logo-dialog
            v-model="uploadDialogOpen"
            @change="logoChanged"
            title="Upload logo"
            :description="uploadDescription"
            :width="900"
            :height="300"
        ></upload-logo-dialog>

        <localizations-editor v-model="localizationEditorModel"
                              :sub-panel="data"
        ></localizations-editor>

        <members-app-dialog v-model="membersAppDialogModel"
                            :sub-panel="data"
        ></members-app-dialog>

        <registration-survey-link-dialog v-model="registrationLinksDialogModel"
                                         :sub-panel="data"
        ></registration-survey-link-dialog>
    </div>
</template>

<script>

import Json from 'vue-json-pretty';
import ToolbarPage from '../../../components/UI/ToolbarPage';
import StatBoxNumerical from '../../../components/UI/StatBoxNumerical';
import Locale from '../../../components/UI/Locale';
import LocaleFlag from '../../../components/UI/LocaleFlag';
import UploadLogoDialog from '../../../components/UI/ImageUploaderDialog';
import LandingPageConfigurator from "./LandingPageEditor";
import EmailThemeConfigurator from "./EmailTemplateEditor";
import TitleCard from "../../../components/UI/TitleCard";
import LocalizationsEditor from "../../../components/UI/LocalizationsEditor";
import MembersAppDialog from "./MembersAppDialog";
import RegistrationSurveyLinkDialog from "./RegistrationSurveyLinkDialog";

export default {

    components: {
        RegistrationSurveyLinkDialog,
        MembersAppDialog,
        Json,
        ToolbarPage,
        StatBoxNumerical,
        Locale,
        LocaleFlag,
        UploadLogoDialog,
        LandingPageConfigurator,
        EmailThemeConfigurator,
        TitleCard,
        LocalizationsEditor
    },

    props: {
        data: {
            required: true,
            type: Object,
        },
        uploading: {
            required: true,
            type: Boolean,
            default: false,
        }
    },

    data() {
        return {
            debug: false,
            landingEditorKey: 1,
            emailEditorKey: 1,
            editButtonVisible: false,
            uploadDialogOpen: false,
            imageData: null,
            configureLandingModel: false,
            configureEmailModel: false,
            localizationEditorModel: false,
            membersAppDialogModel: false,
            registrationLinksDialogModel: false,
            uploadDescription:
                "Recommended logo size is 900px x 300px or larger with 3/1 aspect ratio. Either " +
                "JPG or PNG formats are accepted. We recommend using PNG logo with a transparent " +
                "background for best results.",
        }
    },

    computed: {
        computedCommunityUrl() {
            if (this.data.app_url) {
                return 'https://' + window.location.hostname + '/app/' + this.data.id + '/' + this.data.locales[0].code;

            } else {
                return this.data.url;
            }
        }
    },

    methods: {

        communityUrlClicked() {
            this.sendToUrl(this.computedCommunityUrl, true);
        },

        showEditLogoButton(bool) {
            // Use time out to prevent button flickering
            if (!bool) {
                this.timeOutFunction = setTimeout(() => {
                    this.editButtonVisible = false;
                }, 1000);
            } else {
                clearTimeout(this.timeOutFunction);
                this.editButtonVisible = true;
            }
        },

        editLogoClicked() {
            this.uploadDialogOpen = true;
        },

        openSettings() {
            this.$router.push({name: 'subpanel-edit', params: {id: this.data.id}});
        },

        emitChanges() {
            this.$emit('change', this.data);
        },

        editEmailsClicked() {
            this.emailEditorKey++;

            this.$nextTick(() => {
                this.configureEmailModel = true
            });
        },

        editLandingPagesClicked() {
            this.landingEditorKey++;

            this.$nextTick(() => {
                this.configureLandingModel = true;
            });
        },

        editTranslationsClicked() {
            this.localizationEditorModel = true;
        },

        logoChanged(data) {

            const formData = new FormData();

            formData.append('id', this.data.id);
            formData.append('logo', data.blob, 'logo.png');

            // Display immediately
            this.imageData = data.imageData;
            this.uploadDialogOpen = false;

            this.$emit('logo', formData);
        },

        registrationLinksClicked() {

            if (this.data.registration_survey && this.data.registration_survey.enabled) {
                this.registrationLinksDialogModel = true;
            } else {
                Bus.$emit('flash-message', {message: 'Enable Registration Survey in the sub panel settings. Click on the GEAR -icon top right.'})
            }
        },

        membersAppClicked() {
            if (this.data.app_enabled) {
                this.membersAppDialogModel = true;
            } else {
                Bus.$emit('flash-message', {message: 'Enable Members App in the sub panel settings. Click on the GEAR -icon top right.'})
            }
        }
    }
}
</script>

<style scoped>
.pointer {
    cursor: pointer;
}
</style>
